/**
 * Single-series line chart, hand-rolled SVG.
 *
 * Not Recharts, deliberately: it costs ~95 KB gzipped and this needs one line,
 * markers, and a crosshair. On a phone app that's a bad trade for two simple
 * charts, and hand-rolling gives exact control over the spec below.
 *
 * Built to the dataviz rules:
 *  - ONE y-axis. Weight and volume are different scales, so they are two
 *    separate charts, never a dual axis. (That's the single most common chart
 *    mistake and a workout app tempts you into it constantly.)
 *  - One series, so no legend box — the title names it.
 *  - 2px line, 8px markers, recessive grid.
 *  - Direct labels on the LATEST point and PR points only, never every point.
 *  - Crosshair + tooltip, which is mandatory for a line chart rather than a
 *    nice-to-have.
 *  - Text wears ink tokens, never the series colour.
 *  - Dark mode uses selected steps from the same ramps, validated against the
 *    app's dark surface — not an inverted light palette.
 *
 * Palette validated with the dataviz validator against both app surfaces:
 *   light #2a78d6 / #1baf7a on #fafaf8, dark #3987e5 / #199e70 on #16161a.
 *   All checks pass; light-mode aqua sits below 3:1 contrast, for which the
 *   required relief is the visible direct labels this chart always draws.
 */

import { useMemo, useRef, useState } from 'react'

import { parseIso } from '../lib/time'

export type Point = {
  date: string
  value: number | null
  isPR?: boolean
}

type Props = {
  title: string
  points: Point[]
  unit: string
  /** 'primary' = blue (strength), 'secondary' = aqua (volume). */
  tone?: 'primary' | 'secondary'
  height?: number
  /** Formats the value in tooltips and labels. */
  format?: (v: number) => string
}

const W = 320          // viewBox width; the SVG scales to its container
const PAD_L = 8
const PAD_R = 44       // room for the direct label on the latest point
const PAD_T = 14
const PAD_B = 22

export function ProgressChart({
  title,
  points,
  unit,
  tone = 'primary',
  height = 160,
  format,
}: Props) {
  const [hover, setHover] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const data = useMemo(
    () => points.filter((p): p is Point & { value: number } => p.value != null),
    [points],
  )

  const fmt = format ?? ((v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1)))

  const geom = useMemo(() => {
    if (data.length === 0) return null

    const values = data.map((d) => d.value)
    let min = Math.min(...values)
    let max = Math.max(...values)

    // Never zero-base a strength chart. Progress is a few percent over months;
    // anchoring at zero flattens a real 15 kg gain into a straight line. Pad
    // the range instead so movement is visible without exaggerating it.
    const span = max - min || Math.max(1, max * 0.1)
    min -= span * 0.15
    max += span * 0.15

    const plotW = W - PAD_L - PAD_R
    const plotH = height - PAD_T - PAD_B

    const x = (i: number) =>
      PAD_L + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW)
    const y = (v: number) => PAD_T + plotH - ((v - min) / (max - min)) * plotH

    return { x, y, min, max, plotH }
  }, [data, height])

  if (data.length === 0 || !geom) {
    return (
      <figure style={{ margin: '0 0 1.25rem' }}>
        <figcaption style={{ fontSize: '.95rem', marginBottom: '.4rem' }}>{title}</figcaption>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <span className="muted">Not enough data yet.</span>
        </div>
      </figure>
    )
  }

  const { x, y } = geom
  const path = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(d.value)}`).join(' ')

  const last = data[data.length - 1]
  const lastIdx = data.length - 1
  const active = hover != null ? data[hover] : null

  const strokeVar = tone === 'primary' ? 'var(--chart-1)' : 'var(--chart-2)'

  const onMove = (clientX: number) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const rel = ((clientX - rect.left) / rect.width) * W
    // Nearest point, not "the one under the cursor" — a 2px line is far
    // narrower than a fingertip.
    let best = 0
    let bestDist = Infinity
    for (let i = 0; i < data.length; i++) {
      const d = Math.abs(x(i) - rel)
      if (d < bestDist) {
        best = i
        bestDist = d
      }
    }
    setHover(best)
  }

  return (
    <figure style={{ margin: '0 0 1.25rem' }}>
      <figcaption style={{ fontSize: '.95rem', marginBottom: '.4rem' }}>
        {title}{' '}
        <span className="muted" style={{ fontWeight: 400 }}>({unit})</span>
      </figcaption>

      <div className="card" style={{ padding: '.5rem .25rem .25rem', position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={`${title} over time. Latest ${fmt(last.value)} ${unit}.`}
          style={{ display: 'block', touchAction: 'pan-y' }}
          onMouseMove={(e) => onMove(e.clientX)}
          onMouseLeave={() => setHover(null)}
          onTouchStart={(e) => onMove(e.touches[0].clientX)}
          onTouchMove={(e) => onMove(e.touches[0].clientX)}
          onTouchEnd={() => setHover(null)}
        >
          {/* Recessive gridlines: three, unlabelled except the extremes. */}
          {[0, 0.5, 1].map((t) => {
            const gy = PAD_T + t * geom.plotH
            return (
              <line
                key={t}
                x1={PAD_L}
                x2={W - PAD_R}
                y1={gy}
                y2={gy}
                stroke="var(--border)"
                strokeWidth={1}
              />
            )
          })}

          {active && (
            <line
              x1={x(hover!)}
              x2={x(hover!)}
              y1={PAD_T}
              y2={PAD_T + geom.plotH}
              stroke="var(--border)"
              strokeWidth={1}
            />
          )}

          <path d={path} fill="none" stroke={strokeVar} strokeWidth={2}
                strokeLinejoin="round" strokeLinecap="round" />

          {/* Markers. A 2px surface ring keeps overlapping points legible. */}
          {data.map((d, i) => {
            const isEnd = i === lastIdx
            const emphasise = isEnd || d.isPR || i === hover
            if (!emphasise && data.length > 40) return null
            return (
              <circle
                key={d.date}
                cx={x(i)}
                cy={y(d.value)}
                r={emphasise ? 4.5 : 2.5}
                fill={d.isPR ? 'var(--surface)' : strokeVar}
                stroke={strokeVar}
                strokeWidth={d.isPR ? 2.5 : 2}
              />
            )
          })}

          {/* Direct label on the latest point only — never a number on every
              point, which is the fastest way to make a chart unreadable. */}
          <text
            x={x(lastIdx) + 8}
            y={y(last.value) + 4}
            fill="var(--text)"
            fontSize={11}
            fontWeight={600}
          >
            {fmt(last.value)}
          </text>

          <text x={PAD_L} y={height - 6} fill="var(--muted)" fontSize={10}>
            {shortDate(data[0].date)}
          </text>
          {data.length > 1 && (
            <text x={W - PAD_R} y={height - 6} fill="var(--muted)" fontSize={10}
                  textAnchor="end">
              {shortDate(last.date)}
            </text>
          )}
        </svg>

        {active && (
          <div
            style={{
              position: 'absolute',
              top: 4,
              left: 8,
              right: 8,
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '.8rem',
              pointerEvents: 'none',
            }}
          >
            <span className="muted">{longDate(active.date)}</span>
            <span style={{ fontWeight: 600 }}>
              {fmt(active.value)} {unit}
              {active.isPR && <span className="muted"> · PR</span>}
            </span>
          </div>
        )}
      </div>
    </figure>
  )
}

function shortDate(iso: string): string {
  const d = parseIso(iso)
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function longDate(iso: string): string {
  const d = parseIso(iso)
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' })
}
